import { Col, Modal, Row, Typography, Table } from 'antd';

import { useEffect, useState } from 'react';

import { ColumnsType } from 'antd/lib/table';

import { useIntl } from 'react-intl';

import { Key } from 'antd/es/table/interface';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { ModalProps } from '@/types/ModalProps';

import {
  DocumentType,
  ListDocumentTypesQuery,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import { useDocumentTypes } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import LoadMoreSummary from '../LoadMoreSummary';
import DocumentTypesSelectedFilters from './DocumentTypesSelectedFilters';

interface SelectedDocumentTypesProps extends ModalProps {
  finishCallback?: (documentTypes?: DocumentType[]) => void;
  selectedDocumentTypes?: DocumentType[];
}

const DocumentTypesSelectedModal: React.FC<SelectedDocumentTypesProps> = ({
  visible,
  finishCallback,
  handleVisible,
  selectedDocumentTypes = [],
}) => {
  const intl = useIntl();

  const { Title } = Typography;

  const [queryParams, setQueryParams] = useState<ListDocumentTypesQuery>({
    order: 'ASC',
  });

  const [selectedDocumentTypesKeys, setSelectedDocumentTypesKeys] = useState<
    Key[]
  >([]);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useDocumentTypes(queryParams);

  const documentTypes = flatInfiniteData(data);

  useEffect(() => {
    if (visible) {
      setSelectedDocumentTypesKeys(
        selectedDocumentTypes.map((documentType) => documentType.id),
      );
    }
  }, [visible]);

  const handleOk = (): void => {
    const documentTypes = getSelectedDocumentTypes();

    if (finishCallback) {
      finishCallback(documentTypes);
    }

    handleCancel();
  };

  const handleCancel = (): void => {
    setQueryParams({});

    setSelectedDocumentTypesKeys([]);

    handleVisible();
  };

  const columns: ColumnsType<DocumentType> = [
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'documentTypes.list.header.description',
      }),
      ellipsis: true,
      width: 200,
    },
    {
      dataIndex: 'initials',
      title: intl.formatMessage({ id: 'documentTypes.list.header.initials' }),
      ellipsis: true,
      width: 220,
    },
  ];

  const handleDocumentTypesSelect = (selectedRowKeys: Key[]) => {
    setSelectedDocumentTypesKeys(selectedRowKeys);
  };

  const getSelectedDocumentTypes = (): DocumentType[] => {
    const result = documentTypes.filter((documentType) => {
      return selectedDocumentTypesKeys.includes(documentType.id);
    });

    return result;
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: 'documentTypes.modal.selectedDocumentTypes.title.add',
      })}
      visible={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={800}
    >
      <Row gutter={16}>
        <Col flex="1">
          <div style={{ width: '100%' }}>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>
                {intl.formatMessage({
                  id: 'documentTypes.modal.selectedDocumentTypes.search',
                })}
              </Title>
            </div>

            <DocumentTypesSelectedFilters setQueryParams={setQueryParams} />

            <Table<DocumentType>
              columns={columns}
              dataSource={documentTypes}
              loading={isFetching}
              rowKey={(row) => row.id}
              pagination={false}
              summary={() =>
                hasNextPage ? (
                  <LoadMoreSummary
                    onClick={fetchNextPage}
                    columnsCount={columns.length + 1}
                  />
                ) : null
              }
              rowSelection={{
                selectedRowKeys: selectedDocumentTypesKeys,
                onChange: handleDocumentTypesSelect,
              }}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default DocumentTypesSelectedModal;
